import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { getAdminLeadsSummary } from "@/actions/reportsAdmin";
import { Download, School, Users, Eye, Search } from "lucide-react";
import ReportHeader from "../components/ReportHeader";

interface SchoolLeadSummary {
  slate_id: number;
  slate_name: string;
  views: number;
  areaName: string;
  cityName: string;
  total_leads: number;
  [key: string]: string | number; // Dynamic source columns
}

const formatNumber = (num: number): string => num?.toLocaleString() || "0";

const SchoolSummaryReport: React.FC = () => {
  const [fromDate, setFromDate] = useState<string>(moment().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [schoolSummary, setSchoolSummary] = useState<SchoolLeadSummary[]>([]);
  const [includeDuplicates, setIncludeDuplicates] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter schools based on search term (searches name, city, area)
  const filteredSchools = useMemo(() => {
    if (!searchTerm.trim()) return schoolSummary;
    const term = searchTerm.toLowerCase();
    return schoolSummary.filter(school =>
      school.slate_name?.toLowerCase().includes(term) ||
      school.cityName?.toLowerCase().includes(term) ||
      school.areaName?.toLowerCase().includes(term)
    );
  }, [schoolSummary, searchTerm]);

  // Extract dynamic source columns from data (excluding fixed columns)
  const fixedColumns = ['slate_id', 'slate_name', 'views', 'areaName', 'cityName', 'total_leads'];
  const sourceColumns = useMemo(() => {
    if (schoolSummary.length === 0) return [];
    const firstRow = schoolSummary[0];
    const columns = Object.keys(firstRow).filter(key => !fixedColumns.includes(key));

    // Priority order: Google, Website, Facebook, Organic, then remaining
    const priorityOrder = ['Google', 'Website', 'Facebook', 'Organic'];
    const orderedColumns: string[] = [];

    // Add priority columns first (if they exist)
    priorityOrder.forEach(col => {
      const index = columns.indexOf(col);
      if (index > -1) {
        orderedColumns.push(col);
        columns.splice(index, 1);
      }
    });

    // Add remaining columns
    return [...orderedColumns, ...columns];
  }, [schoolSummary]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAdminLeadsSummary({
        fromDate,
        toDate,
        clientId: 9,
        includeDuplicates: includeDuplicates ? 1 : 0,
      });
      if (response.success) setSchoolSummary((response.data as SchoolLeadSummary[]) || []);
    } catch (error) {
      console.error("Error fetching school summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["School Name", "City", "Area", "Views", "Total Leads", ...sourceColumns];
    const csvRows = [headers.join(",")];
    schoolSummary.forEach((s) => {
      const sourceValues = sourceColumns.map(col => s[col] || 0);
      csvRows.push([
        `"${s.slate_name}"`, `"${s.cityName || ""}"`, `"${s.areaName || ""}"`,
        s.views, s.total_leads, ...sourceValues
      ].join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `school-summary-${fromDate}_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalLeads = schoolSummary.reduce((sum, s) => sum + (s.total_leads || 0), 0);
  const totalViews = schoolSummary.reduce((sum, s) => sum + (s.views || 0), 0);

  if (loading) {
    return (
      <div className="h-full">
        <ReportHeader title="School Summary" subtitle="School-wise leads breakdown" icon={<School className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData}>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includeDuplicates} onChange={(e) => setIncludeDuplicates(e.target.checked)} className="rounded" />
            Include Duplicates
          </label>
        </ReportHeader>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ReportHeader title="School Summary" subtitle="School-wise leads breakdown" icon={<School className="h-5 w-5 text-white" />} fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate} onApplyFilter={fetchData}>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={includeDuplicates} onChange={(e) => setIncludeDuplicates(e.target.checked)} className="rounded" />
          Include Duplicates
        </label>
      </ReportHeader>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-brandColor">
              <div className="flex justify-between items-center">
                <div><div className="text-sm gray-text">Total Schools</div><div className="text-2xl font-bold">{schoolSummary.length}</div></div>
                <School className="h-8 w-8 text-brandColor" />
              </div>
            </div>
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="flex justify-between items-center">
                <div><div className="text-sm gray-text">Total Leads</div><div className="text-2xl font-bold">{formatNumber(totalLeads)}</div></div>
                <Users className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-purple-500">
              <div className="flex justify-between items-center">
                <div><div className="text-sm gray-text">Total Views</div><div className="text-2xl font-bold">{formatNumber(totalViews)}</div></div>
                <Eye className="h-8 w-8 text-purple-500" />
              </div>
            </div>
          </div>

          {/* School Summary Table */}
          <div className="background-container rounded-lg shadow">
            <div className="bg-gradient-to-r from-brandColor to-brand-hover px-6 py-4 rounded-t-lg flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2"><School className="h-5 w-5" />School-wise Leads Summary</h3>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search school, city, area..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 text-sm rounded-lg border-0 focus:ring-2 focus:ring-white/50 w-64"
                  />
                </div>
                <button onClick={exportToCSV} className="inline-flex items-center gap-2 px-4 py-2 bg-white text-brandColor text-sm font-medium rounded-lg hover:bg-gray-100"><Download className="h-4 w-4" />Export CSV</button>
              </div>
            </div>
            <div className="px-4 pb-4 overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">School</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">City</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Area</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Views</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Total</th>
                    {sourceColumns.map((col) => (
                      <th key={col} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSchools.map((school) => (
                    <tr key={school.slate_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{school.slate_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{school.cityName || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{school.areaName || "-"}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatNumber(school.views)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">{formatNumber(school.total_leads)}</td>
                      {sourceColumns.map((col) => (
                        <td key={col} className="px-4 py-3 text-sm text-gray-500">{Number(school[col]) || 0}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolSummaryReport;
